function getNextExecution (cronExpr, fromDate = new Date()) {
  const parts = cronExpr.trim().split(/\s+/)
  if (parts.length !== 5) return null
  const [minuteStr, hourStr, dayStr, monthStr, dayOfWeekStr] = parts

  const parseField = (field, min, max) => {
    if (field === '*') {
      const arr = []
      for (let i = min; i <= max; i++) arr.push(i)
      return arr
    }
    if (field.includes('/')) {
      if (field === '*/0') {
        throw new Error('Invalid value: */0')
      }
      const step = parseInt(field.split('/')[1], 10)
      const arr = []
      for (let i = min; i <= max; i += step) arr.push(i)
      return arr
    }
    return field.split(',').map(Number)
  }

  const allowedMinutes = parseField(minuteStr, 0, 59).sort((a, b) => a - b)
  const allowedHours = parseField(hourStr, 0, 23).sort((a, b) => a - b)
  const allowedDays = parseField(dayStr, 1, 31).sort((a, b) => a - b)
  const allowedMonths = parseField(monthStr, 1, 12).sort((a, b) => a - b)
  const allowedDaysOfWeek = parseField(dayOfWeekStr, 0, 6).sort((a, b) => a - b)

  // Get the last day of the month
  const lastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate()
  }

  // Get the next value from the allowed list.
  // If the current value is not in the list, return the first value.
  function getNextValue (current, allowed) {
    for (const val of allowed) {
      if (val >= current) return { value: val, rollover: false }
    }
    return { value: allowed[0], rollover: true }
  }

  let candidate = new Date(fromDate.getTime())
  candidate.setSeconds(0, 0)
  candidate.setMinutes(candidate.getMinutes() + 1)

  // Main loop
  while (true) {
    const year = candidate.getFullYear()
    const month = candidate.getMonth() + 1
    const day = candidate.getDate()
    const hour = candidate.getHours()
    const minute = candidate.getMinutes()

    // 1. Month adjustment
    const { value: nextMonth, rollover: monthRollover } = getNextValue(month, allowedMonths)
    if (monthRollover && nextMonth < month) {
      candidate = new Date(year + 1, nextMonth - 1, 1, allowedHours[0], allowedMinutes[0], 0)
      continue
    }
    if (nextMonth > month) {
      candidate = new Date(year, nextMonth - 1, 1, allowedHours[0], allowedMinutes[0], 0)
      continue
    }

    // 2. Day adjustment
    const lastDay = lastDayOfMonth(year, month)
    const validDays = allowedDays.filter(d => d <= lastDay)
    if (validDays.length === 0) {
      candidate = new Date(year, candidate.getMonth() + 1, 1, allowedHours[0], allowedMinutes[0], 0)
      continue
    }
    const { value: nextDay, rollover: dayRollover } = getNextValue(day, validDays)
    if (dayRollover && nextDay < day) {
      candidate = new Date(year, candidate.getMonth() + 1, validDays[0], allowedHours[0], allowedMinutes[0], 0)
      continue
    }
    if (nextDay > day) {
      candidate = new Date(year, candidate.getMonth(), nextDay, allowedHours[0], allowedMinutes[0], 0)
      continue
    }

    // 3. Hour adjustment
    const { value: nextHour, rollover: hourRollover } = getNextValue(hour, allowedHours)
    if (hourRollover && nextHour < hour) {
      candidate = new Date(year, candidate.getMonth(), candidate.getDate() + 1, allowedHours[0], allowedMinutes[0], 0)
      continue
    }
    if (nextHour > hour) {
      candidate = new Date(year, candidate.getMonth(), candidate.getDate(), nextHour, allowedMinutes[0], 0)
      continue
    }

    // 4. Minute adjustment
    const { value: nextMinute, rollover: minuteRollover } = getNextValue(minute, allowedMinutes)
    if (minuteRollover && nextMinute < minute) {
      candidate = new Date(year, candidate.getMonth(), candidate.getDate(), candidate.getHours() + 1, allowedMinutes[0], 0)
      continue
    }
    if (nextMinute > minute) {
      candidate = new Date(year, candidate.getMonth(), candidate.getDate(), candidate.getHours(), nextMinute, 0)
    }

    // 5. Day of week adjustment
    if (allowedDaysOfWeek.includes(candidate.getDay())) {
      return candidate
    } else {
      candidate = new Date(year, candidate.getMonth(), candidate.getDate() + 1, allowedHours[0], allowedMinutes[0], 0)
      continue
    }
  }
}

function parseCrontabToTable (crontab) {
  const [minute, hour, dayMonth, month, dayWeek] = crontab.trim().split(/\s+/)

  const parseField = (field) => {
    return {
      value: field === '*' ? '' : (field.includes('/') ? field.split('/')[1] : field),
      repeat: field.includes('*/')
    }
  }

  return {
    minutes: parseField(minute),
    hours: parseField(hour),
    dayMonths: parseField(dayMonth),
    months: parseField(month),
    dayWeeks: dayWeek === '0' ? parseField('7') : parseField(dayWeek)
  }
}

function buildCrontab (data) {
  const buildField = (field) => {
    if (field === 'dayWeeks' && data[field]?.value === '7') {
      return '0'
    }
    if (data[field]?.value === null) {
      return '*'
    }
    if (data[field]?.value !== null && data[field]?.repeat) {
      return `*/${data[field].value}`
    }
    if (data[field]?.value === 0) {
      return '0'
    }
    return data[field]?.value || '*'
  }

  const minute = buildField('minutes')
  const hour = buildField('hours')
  const dayMonth = buildField('dayMonths')
  const month = buildField('months')
  const dayWeek = buildField('dayWeeks')

  return `${minute} ${hour} ${dayMonth} ${month} ${dayWeek}`
}

function checkCrontabFormat (crontab) {
  const crontabParts = crontab.split(' ')
  if (crontabParts.length !== 5) {
    return false
  }

  const validField = (field, min, max) => {
    if (field === '*') {
      return true
    }
    if (field.includes('/')) {
      const step = parseInt(field.split('/')[1], 10)
      return !isNaN(step) && step > 0
    }
    const value = parseInt(field, 10)
    return !isNaN(value) && value >= min && value <= max
  }

  return validField(crontabParts[0], 0, 59) &&
    validField(crontabParts[1], 0, 23) &&
    validField(crontabParts[2], 1, 31) &&
    validField(crontabParts[3], 1, 12) &&
    validField(crontabParts[4], 1, 7)
}

export { getNextExecution, parseCrontabToTable, buildCrontab, checkCrontabFormat }
